/**
 * Server History Persistence
 *
 * Stores last 10 successfully connected servers in localStorage.
 * Mirrors Java dview CookieModel historyList: max 10, dedup by serial, MRU ordering.
 *
 * Entry shape: { bSerial, sName, sIP, sLocalIP, bPort, sHostname, lastConnected }
 *
 * NOTE: We intentionally do NOT store bTimestamp. Server heartbeat timestamps become
 * stale immediately and cause servers to falsely appear "offline" when loaded from
 * history. Use lastConnected (client-side Date.now()) for recency display only.
 */

import { debugLog } from './debug';

const STORAGE_KEY = 'dview_server_history';
const MAX_ENTRIES = 10;

/**
 * Get server history array, most-recent-first.
 */
export const getServerHistory = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    debugLog('[serverHistory] Failed to read history:', e.message);
    return [];
  }
};

/**
 * Add a server to history. Deduplicates by bSerial, prepends (MRU), max 10 entries.
 * Accepts a server object from Monarch, getStartup, or connectedServers — normalizes shape.
 */
export const addServerToHistory = (serverInfo) => {
  if (!serverInfo || !serverInfo.bSerial) return;

  const entry = {
    bSerial: serverInfo.bSerial,
    sName: serverInfo.sName || 'Unknown',
    sIP: serverInfo.sIP || '',
    sLocalIP: serverInfo.sLocalIP || '',
    bPort: serverInfo.bPort || 80,
    sHostname: serverInfo.sHostname || '',
    lastConnected: Date.now()
  };

  const history = getServerHistory().filter(h => h.bSerial !== entry.bSerial);
  history.unshift(entry);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(0, MAX_ENTRIES)));
    debugLog('[serverHistory] Added to history:', entry.sName, `(${entry.bSerial})`);
  } catch (e) {
    debugLog('[serverHistory] Failed to save history:', e.message);
  }
};

/**
 * Remove a server from history by serial number.
 */
export const removeServerFromHistory = (bSerial) => {
  const history = getServerHistory().filter(h => h.bSerial !== bSerial);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (e) {
    debugLog('[serverHistory] Failed to save history:', e.message);
  }
};

/**
 * Clear all server history.
 */
export const clearServerHistory = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    debugLog('[serverHistory] Failed to clear history:', e.message);
  }
};
